import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlassIcon, XIcon, CaretDownIcon, CheckIcon } from "@phosphor-icons/react";

/**
 * Selector multiselección con buscador y tags removibles.
 *
 * Props:
 *   label            — texto sobre el campo
 *   items            — [{id, label, sub?}] o [string]
 *   value            — array de ids seleccionados
 *   onChange         — (ids[]) => void
 *   placeholder      — texto cuando no hay selección
 *   testid           — data-testid base
 *   maxTags          — cuántos tags mostrar antes de "+N"
 *   searchKeys       — llaves adicionales para búsqueda (ej: ["codigo","rtn"])
 */
export default function MultiSelectCombo({
  label,
  items = [],
  value = [],
  onChange,
  placeholder = "Todos",
  testid,
  maxTags = 3,
  searchKeys = [],
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  // Normalizar items → siempre {id, label, ...extra}
  const norm = useMemo(
    () =>
      items.map((it) =>
        typeof it === "string" || typeof it === "number"
          ? { id: String(it), label: String(it) }
          : { ...it, id: String(it.id ?? it.value ?? it.codigo ?? it.nombre), label: it.label ?? it.nombre ?? String(it.id ?? "") }
      ),
    [items]
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return norm;
    const s = q.trim().toLowerCase();
    return norm.filter((it) => {
      if (it.label?.toLowerCase().includes(s)) return true;
      for (const k of searchKeys) {
        if (String(it[k] ?? "").toLowerCase().includes(s)) return true;
      }
      return false;
    });
  }, [norm, q, searchKeys]);

  const selectedSet = useMemo(() => new Set(value.map(String)), [value]);
  const selectedItems = useMemo(() => norm.filter((it) => selectedSet.has(it.id)), [norm, selectedSet]);

  const toggle = (id) => {
    const s = String(id);
    const next = selectedSet.has(s) ? value.filter((v) => String(v) !== s) : [...value, s];
    onChange(next);
  };
  const clear = () => onChange([]);
  const selectAll = () => onChange(filtered.map((it) => it.id));

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const shown = selectedItems.slice(0, maxTags);
  const extra = selectedItems.length - shown.length;

  return (
    <div className="flex flex-col gap-1 relative" ref={ref}>
      {label && <span className="text-[9px] uppercase tracking-wider text-text-muted">{label}</span>}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testid ? `${testid}-trigger` : undefined}
        className="bg-app border border-border rounded-md px-2.5 py-2 text-xs min-h-[36px] text-left flex flex-wrap items-center gap-1 hover:border-agro-green/40 min-w-[160px]"
      >
        {selectedItems.length === 0 ? (
          <span className="text-text-muted">{placeholder}</span>
        ) : (
          <>
            {shown.map((it) => (
              <span
                key={it.id}
                className="bg-agro-green/15 text-agro-green rounded px-1.5 py-0.5 text-[10px] flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(it.id);
                }}
              >
                {it.label}
                <XIcon size={10} weight="bold" />
              </span>
            ))}
            {extra > 0 && (
              <span className="text-agro-green text-[10px] font-medium">+{extra}</span>
            )}
          </>
        )}
        <CaretDownIcon size={12} className="ml-auto text-text-muted" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-[280px] bg-card border border-border/60 rounded-md shadow-2xl z-50 overflow-hidden"
          data-testid={testid ? `${testid}-panel` : undefined}>
          <div className="p-2 border-b border-border/60 relative">
            <MagnifyingGlassIcon size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar…"
              className="w-full bg-app border border-border rounded pl-6 pr-2 py-1.5 text-xs"
              data-testid={testid ? `${testid}-search` : undefined}
            />
          </div>
          <div className="flex justify-between px-3 py-1.5 border-b border-border/60 text-[10px]">
            <button onClick={selectAll} className="text-agro-green hover:underline"
              data-testid={testid ? `${testid}-selectall` : undefined}>Seleccionar visibles</button>
            <button onClick={clear} className="text-text-muted hover:text-white"
              data-testid={testid ? `${testid}-clear` : undefined}>Limpiar</button>
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-text-muted text-xs">Sin resultados</div>
            ) : (
              filtered.map((it) => (
                <button
                  key={it.id}
                  onClick={() => toggle(it.id)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-white/[0.04] text-xs ${
                    selectedSet.has(it.id) ? "bg-agro-green/5 text-white" : "text-text-secondary"
                  }`}
                  data-testid={testid ? `${testid}-opt-${it.id}` : undefined}
                >
                  <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center ${
                    selectedSet.has(it.id) ? "bg-agro-green border-agro-green" : "border-border"
                  }`}>
                    {selectedSet.has(it.id) && <CheckIcon size={9} weight="bold" className="text-black" />}
                  </span>
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.sub && <span className="text-[9px] text-text-muted">{it.sub}</span>}
                </button>
              ))
            )}
          </div>
          {selectedItems.length > 0 && (
            <div className="p-2 border-t border-border/60 text-[10px] text-text-muted text-center">
              {selectedItems.length} seleccionado{selectedItems.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
